"use client";

import * as React from "react";
import Link from "next/link";
import { ChartCard, LiquidityPie } from "@/components";
import { ChartPieIcon } from "@/icons";
import { useHomeLiquidityPreview } from "@/hooks/use-liquidity";
import { HEX_VARIANT_PILL } from "@/constants/chart-palette";

type PairSlice = {
  name: string;
  liquidity: number;
  percentage: number;
};

function aggregateByHexVariant(rows: PairSlice[]): PairSlice[] {
  let ehex = 0;
  let phex = 0;

  for (const row of rows) {
    const label = row.name.toUpperCase();
    if (label.startsWith("EHEX")) ehex += row.liquidity;
    else if (label.startsWith("PHEX")) phex += row.liquidity;
  }

  const total = ehex + phex || 1;
  return [
    {
      name: "eHEX",
      liquidity: ehex,
      percentage: (ehex / total) * 100,
    },
    {
      name: "pHEX",
      liquidity: phex,
      percentage: (phex / total) * 100,
    },
  ].filter((row) => row.liquidity > 0);
}

const PREVIEW_COLORS = [
  HEX_VARIANT_PILL.ehex.color,
  HEX_VARIANT_PILL.phex.color,
] as const;

export function Liquidity() {
  const { data, isLoading } = useHomeLiquidityPreview();
  const chartData = React.useMemo(
    () => aggregateByHexVariant(Array.isArray(data) ? data : []),
    [data],
  );

  return (
    <Link href="/liquidity" className="block h-full cursor-pointer">
      <ChartCard
        title="Liquidity"
        icon={<ChartPieIcon size={20} color="#FF0F6F" />}
        isLoading={isLoading}
        accent="eHEX · pHEX"
        description="Combined HEX liquidity across PulseChain and Ethereum"
      >
        <LiquidityPie
          data={chartData}
          colors={PREVIEW_COLORS}
          isLoading={isLoading}
        />
      </ChartCard>
    </Link>
  );
}
